import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from '@navi/web-ui/lib/primitives/Toast/index';
import styles from './App.module.scss';
import AppRouter from './AppRouter';
import { AppConfig } from './types/AppConfig';
import axiosInstance, { ApiKeys, getApiUrl, registerNavigateAndDispatch } from './utils/ApiHelper';
import {
  GLOBAL,
  POST_MESSAGE_IDENTIFIER,
  POST_MESSAGE_PARAMS,
  setScreenResolution
} from './constants/Global';
import AppBlockerOverlay from './AppBlockerOverlay';
import { RootState, store } from './store';
import { poll } from './utils/polling';
import { BUILD_CHECK_POLL_TIMEOUT, refresh } from './utils/checkBuildTime';
import { getDeviceId } from './utils/FingerPrintJS';
import { syncApiErrors } from './service/SyncApiErrorsService';
import { MILLI_IN_MIN } from './utils/DateHelper';
import { getFCMToken } from './firebase/firebase';
import {
  CosmosSyncBlockStatus,
  setFcmToken,
  setSlashCallStatusFromExtension,
  SlashCallStatus
} from './reducers/commonSlice';
import NotificationsBlockerOverlay from './NotificationBlockerOverlay';
import { LOCAL_STORAGE_KEYS, SESSION_STORAGE_KEYS } from './constants/StorageKeys';
import { pushToSessionStorage } from './utils/StorageUtils';
import { v4 as uuidv4 } from 'uuid';
import useLocalStorageObserver from './hooks/useLocalStorageObserver';
import { AMEYO_AVAILABILITY_KEY, HEARTBEAT_TYPE } from './constants/Common.constants';
import heartbeatService from './service/heartbeatService';
import cx from 'classnames';
import useInternetAvailability from './hooks/useInternetAvailability';
import { WifiOnline } from './assets/icons/WifiOnline';
import { WifiOffline } from './assets/icons/WifiOffline';
import useUpdateEffect from '@cp/hooks/useUpdateEffect';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import CryptoJS from 'crypto-js';
import sendAgentAvailabilityStatusToSlash from '@cp/utils/sendAgentAvailabilityStatusToSlash';
import { ExtensionHandler } from '@cp/utils/extension.utils';
import {
  LONGHORN_SESSION_TOKEN,
  WA_EXTENSION_ID,
  WA_PLUGIN_VERSION
} from './pages/auth/constants/AuthConstants';

import CybertronLogs from '@cybertron/logger/lib/main';
import { getExpiredImages } from './utils/WhatsappSelfieDB';
import { sentryBrowser as Cybertron, setScreenName } from '@cybertron/core/lib/main';
import { returnScreenName } from './layout/utils';
import useNavigationPerformance from './hooks/useNavigationPerformance';
declare global {
  interface Window {
    config: AppConfig;
    clarity: (...args: any[]) => void;
    google: any;
    clientIp?: string;
    alfredSessionId: string;
    ameyoInitialized: boolean;
  }
  interface Navigator {
    userAgentData: {
      platform: string;
    };
  }
}
const releaseId = 'process.env.RELEASE_VERSION';
const alfredSessionId = window?.alfredSessionId;
const sessionId = uuidv4();
if (!import.meta.env.DEV) {
  Cybertron.init({
    releaseId,
    alfredSessionId: alfredSessionId ?? '',
    projectName: 'longhorn-portal',
    env: window?.config?.ENV,
    dsn: window.config.SENTRY_DSN,
    tunnel: window.config.APM_BASE_URL,
    integrations: [Cybertron.browserTracingIntegration()],
    tracesSampleRate: 1.0,
    vertical: 'COLLECTIONS',
    customerId: '',
    metadata: {}
  });

  CybertronLogs.setOptions({
    appName: 'longhorn-portal',
    env: window?.config?.ENV,
    sessionId,
    teamName: 'collections',
    threshold: 10
  });
}

const config = window.config ?? {};

const isMobile = () => {
  if (typeof window?.navigator === 'undefined') return true;

  return window?.navigator?.userAgent.match(/Mobile|iP(hone|od|ad)|Android|BlackBerry|IEMobile/);
};

const shortCircuitThroughENV = () => {
  if (window?.config?.ENV !== 'prod') {
    return true;
  }
  return false;
};
const shouldBlockUserForExtension = (agentRefId: string) => {
  if (shortCircuitThroughENV()) {
    return false;
  }
  return (
    !isMobile() &&
    !window?.config?.EXTENSION_PLUGIN_USERS_LIST?.find(
      refId => refId === CryptoJS.SHA1(agentRefId).toString()
    )
  );
};

const TIME_TO_SEND_HEARTBEAT = 30 * 1000; // 30 seconds

const InternetStatus = ({ enableStickyNote = false, stickyNoteComponent }: any) => {
  const HIDE_TIMEOUT = 3e3;
  const [hide, setHide] = useState(true);
  const { isOnline } = useInternetAvailability();
  useUpdateEffect(() => {
    setHide(false);
    if (isOnline) {
      const timeout = setTimeout(() => {
        setHide(true);
      }, HIDE_TIMEOUT);
      return () => clearTimeout(timeout);
    }
  }, [isOnline]);

  if (hide) {
    if (enableStickyNote && stickyNoteComponent) {
      return stickyNoteComponent();
    }
    return null;
  }

  return (
    <div className={cx('internetStatus', isOnline ? 'online' : 'offline')}>
      {!isOnline && (
        <>
          <WifiOffline /> You’re offline! Some features will be unavailable.
        </>
      )}
      {isOnline && (
        <>
          <WifiOnline /> You’re back online.
        </>
      )}
    </div>
  );
};

const App = () => {
  const phoneNumber = useSelector((store: RootState) => store?.common?.userData.phoneNumber);
  const naviUser = useSelector((store: RootState) => store?.common?.userData.naviUser);
  const userImpersonated = useSelector(
    (store: RootState) => store?.common?.userData.userImpersonated
  );
  const referenceId = useSelector((store: RootState) => store?.common?.userData.referenceId) || '';
  const showStickyNote = useSelector((store: RootState) => store?.common?.showStickyNote);
  const ameyoCampaignId = useSelector((store: RootState) => store?.common?.userData?.campaignId);

  const backgroundImage = useSelector(
    (store: RootState) => store?.longhornTheme?.assets?.backgroundFileUrl
  );
  const rightImage = useSelector((store: RootState) => store?.longhornTheme?.assets?.rightFileUrl);
  const leftImage = useSelector((store: RootState) => store?.longhornTheme?.assets?.leftFileUrl);

  const serverDate = useSelector((state: RootState) => state?.common?.serverDate);

  const [pluginFound, setPluginFound] = useState(false);
  const [userFoundInBlockList, setUserFoundInBlockList] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const isMobilePhone = isMobile();
  const isLonghornThemeEnabled = useSelector(
    (state: RootState) => state.common.isLonghornThemeEnabled
  );

  useNavigationPerformance();

  Notification.requestPermission().then(async permission => {
    if (permission === 'granted') {
      setNotificationPermission(permission);
    }
  });

  const messageHandler = async ({ data }: any) => {
    if (data?.field === POST_MESSAGE_IDENTIFIER.EXTENSION_ID) {
      localStorage.setItem(WA_EXTENSION_ID, data.id);
      setPluginFound(true);
    }

    if (data?.field === POST_MESSAGE_IDENTIFIER.PLUGIN_ALIVE) {
      if (data?.id) {
        localStorage.setItem(WA_EXTENSION_ID, data.id);
      }
      if (data?.version) {
        localStorage.setItem(WA_PLUGIN_VERSION, data.version);
        addClickstreamEvent(CLICKSTREAM_EVENT_NAMES.SEND_EXTENSION_VERSION, {
          version: data.version
        });
      }
      setPluginFound(true);
    }
    if (data?.field === POST_MESSAGE_IDENTIFIER.ON_WHATSAPP_PLUGIN_INIT) {
      window.postMessage({
        type: POST_MESSAGE_IDENTIFIER.SEND_WHATSAPP_CREDENTIALS,
        data: {
          sessionId: localStorage.getItem(LONGHORN_SESSION_TOKEN),
          deviceId: getDeviceId()
        }
      });
    }
    if (data?.type === POST_MESSAGE_IDENTIFIER.AGENT_BREAK_STATUS) {
      sendAgentAvailabilityStatusToSlash(store?.getState()?.common?.isAgentOnline);
    }
    if (data?.type === POST_MESSAGE_IDENTIFIER.LONGHORN_BLOCK_STATUS) {
      const blockStatus = store?.getState()?.common?.cosmosSyncBlockStatus;
      const slashCallStatus = store?.getState()?.common?.slashCallStatusFromExtension;
      const isSlashCallActive = slashCallStatus === SlashCallStatus.ANSWERED;
      if (blockStatus === CosmosSyncBlockStatus.BLOCKED && !isSlashCallActive) {
        ExtensionHandler.blockSlash();
      }
      ExtensionHandler.unblockSlash();
    }
    if (data?.type === POST_MESSAGE_IDENTIFIER.SLASH_CALL_STATUS_MESSAGE) {
      const response = data?.data;
      localStorage.setItem('slashCallStatus', response.data.slashCallStatus);
      dispatch(setSlashCallStatusFromExtension(response.data.slashCallStatus));
    }
  };
  useEffect(() => {
    //window.removeEventListener('message', messageHandler);
    window.postMessage({
      field: POST_MESSAGE_PARAMS.INITIAL_PORTAL_LOGIN
    });

    if (referenceId && shouldBlockUserForExtension(referenceId) && naviUser && !userImpersonated) {
      setUserFoundInBlockList(true);
      return;
    }
    window.addEventListener('message', messageHandler);
  }, [phoneNumber, referenceId]);

  useEffect(() => {
    window.addEventListener('message', messageHandler);
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  registerNavigateAndDispatch(navigate, dispatch);
  const setFCMTokenRedux = (fcmToken: string) => {
    dispatch(setFcmToken(fcmToken));
  };

  const pathName = useLocation();
  useEffect(() => {
    // Only set screen name if we're not in dev mode (where Cybertron isn't initialized)
    if (!import.meta.env.DEV && pathName?.pathname) {
      const finalScreenName = returnScreenName(pathName.pathname);
      if (window?.cybertron && finalScreenName !== 'unknown') {
        setScreenName(finalScreenName);
      }
    }
  }, [pathName]);

  useEffect(() => {
    const promiseFn = () =>
      axiosInstance.get(getApiUrl(ApiKeys.BUILD_TIMESTAMP), {
        headers: { donotHandleError: true }
      });
    const stub = (res: any) => res;
    // interceptor will call "checkBuildTime" so no need to do anything here apart from calling version api at regular inerval.
    poll(promiseFn, stub, BUILD_CHECK_POLL_TIMEOUT, stub);
    getFCMToken(setFCMTokenRedux);
    if (!sessionStorage.getItem(SESSION_STORAGE_KEYS.TAB_ID)) {
      pushToSessionStorage(SESSION_STORAGE_KEYS.TAB_ID, uuidv4());
    }
    // poll(promiseFn, checkBuildTime, 1e4, cb);
  }, []);

  useEffect(() => {
    const syncInterval = syncApiErrors(MILLI_IN_MIN);
    getExpiredImages();
    return () => {
      clearInterval(syncInterval);
    };
  }, []);

  useEffect(() => {
    if (window?.innerWidth && !GLOBAL.SCREEN_WIDTH) {
      setScreenResolution(window?.innerWidth);
    }
  }, [window?.innerWidth]);
  useLocalStorageObserver(LOCAL_STORAGE_KEYS.LONGHORN_SESSION_TOKEN, newValue => {
    if (newValue) {
      location.reload();
    }
  });
  const audioContext = new AudioContext();

  useEffect(() => {
    window.addEventListener('click', () => {
      audioContext.resume();
    });

    return () => {
      window.removeEventListener('click', () => () => {
        audioContext.resume();
      });
    };
  }, []);

  useEffect(() => {
    if (ameyoCampaignId) {
      const timerId = setInterval(() => {
        const isAmeyoAvailable = localStorage.getItem(AMEYO_AVAILABILITY_KEY);
        const ameyoHeartBeatPayload = {
          heartbeatType: HEARTBEAT_TYPE.AGENT_AMEYO_STATUS,
          value: isAmeyoAvailable === 'true' ? 'ONLINE' : 'OFFLINE',
          attributes: {
            campaignId: ameyoCampaignId,
            LH_STATUS: GLOBAL?.LONGHORN_BLOCK_STATUS,
            currentPage: window.location.pathname,
            currentUrl: window.location.href,
            timestamp: serverDate,
            extensionVersion: localStorage.getItem(WA_PLUGIN_VERSION) || ''
          }
        };
        dispatch(heartbeatService([ameyoHeartBeatPayload]));
      }, TIME_TO_SEND_HEARTBEAT);
    }
  }, [ameyoCampaignId]);
  // <Suspense fallback={loading}>
  //   <Routes>
  //     <Route path="*" element={<DefaultLayout />} />
  //   </Routes>
  // </Suspense>

  const stickyNoteComponent = () => {
    return (
      <a onClick={refresh} className="stickyNote" href={window?.location?.href}>
        An update of the app is available, please click this to refresh
      </a>
    );
  };
  return (
    <div>
      {notificationPermission === 'granted' || isMobilePhone || import.meta.env.DEV ? (
        <>
          <div id="modal-box"></div>
          {!pluginFound && userFoundInBlockList && phoneNumber ? (
            <div>
              <AppBlockerOverlay isOpen={true} />
            </div>
          ) : (
            <div
              className={cx({
                [styles.pageLayout]: true,
                [styles.backgroundImage]: isLonghornThemeEnabled
              })}
              style={{
                backgroundImage: isLonghornThemeEnabled
                  ? `url(${leftImage}), url(${rightImage}), url(${backgroundImage})`
                  : 'none'
              }}
            >
              <ToastContainer style={{ width: 'auto', zIndex: 'var(--z-index-toast)' }} />
              <AppRouter />
              <InternetStatus
                enableStickyNote={showStickyNote}
                stickyNoteComponent={stickyNoteComponent}
              />
            </div>
          )}
        </>
      ) : (
        <NotificationsBlockerOverlay isOpen={true} />
      )}
    </div>
  );
};

export default App;
